import { CreateSignalOptions, Signal, WritableSignal, effect, signal } from '@angular/core';
import { Observable, catchError, firstValueFrom, of } from 'rxjs';
import { LOG } from './log';

export const apiErrorSymbol: unique symbol = Symbol('error');
const computedVersionSymbol: unique symbol = Symbol('version');

type ApiSignalMembers = {
  [apiErrorSymbol]: Signal<Error | undefined>;
};

type ComputedApiSignalMembers = ApiSignalMembers & {
  [computedVersionSymbol]: number;
}

/**
 * An extension of Angulars {@link Signal} to also hold a nested signal for occurred errors.
 *
 * The error signal can be retrieved via the {@link apiErrorSymbol} symbol.
 */
export type ApiSignal<T> = Signal<T | undefined> & ApiSignalMembers;
/**
 * A {@link ApiSignal} with a value that can be mutated.
 */
export type WritableApiSignal<T> = WritableSignal<T | undefined> & ApiSignalMembers;
type _ComputedApiSignal<T> = WritableApiSignal<T> & ComputedApiSignalMembers;

/**
 * Sets a value for an {@link ApiSignal} to an {@link Observable}s next emitted value.
 * @param resetValueWhileLoading If `true`, the ApiSignals value will be reset to undefined until the observable emits a value.
 */
export async function refetchApiSignal<T>(source: Observable<T>, apiSignal: WritableApiSignal<T>, resetValueWhileLoading: boolean = true): Promise<void> {
  if (resetValueWhileLoading) {
    apiSignal.set(undefined);
  }
  await fetchObservableForSignal(source, apiSignal, apiSignal[apiErrorSymbol] as WritableSignal<Error | undefined>);
}

/**
 * Creates an {@link ApiSignal} from the given source {@link Observable}.
 * The initial value of the signal will be `undefined` until the source observable emits a value.
 * After that the signal will be set to the emitted value.
 *
 * If the observable produces an error, the value stays undefined and the error will be stored in the nested error signal.
 */
export function toApiSignal<T>(...args: Parameters<typeof toWritableApiSignal<T>>): ApiSignal<T> {
  return toWritableApiSignal(...args);
}

/** See {@link toApiSignal}. */
export function toWritableApiSignal<T>(
  source: Observable<T>,
  options?: CreateSignalOptions<T | undefined> | undefined
): WritableApiSignal<T> {
  const [valueSignal, errorSignal] = createApiSignal(options);

  fetchObservableForSignal(source, valueSignal, errorSignal);

  return valueSignal;
}

/**
 * Creates an {@link ApiSignal}, which always gets assigned the first emitted value from the observables.
 * {@link sourceFn} is registered as an {@link effect} and the results emitted values will assigned to the signal.
 * The value will always belong to the most recent observable produced by sourceFn.
 */
export function computedApiSignal<T>(sourceFn: () => Observable<T> | undefined): ApiSignal<T> {
  const [valueSignal, errorSignal] = createApiSignal<T>();
  (valueSignal as _ComputedApiSignal<T>)[computedVersionSymbol] = 0;

  effect(async () => {
    const source: Observable<T> | undefined = sourceFn();
    const version: number = (valueSignal as _ComputedApiSignal<T>)[computedVersionSymbol] += 1;
    valueSignal.set(undefined);
    errorSignal.set(undefined);

    if (!source) {
      return;
    }

    const value: T | undefined = await firstValueFrom(
      source.pipe(
        catchError(error => {
          if ((valueSignal as _ComputedApiSignal<T>)[computedVersionSymbol] === version) {
            LOG.error(error);
            errorSignal.set(error);
          }
          return of(undefined);
        })
      )
    );

    if ((valueSignal as _ComputedApiSignal<T>)[computedVersionSymbol] === version) {
      valueSignal.set(value);
    }
  }, { allowSignalWrites: true })

  return valueSignal;
}

function createApiSignal<T>(options?: CreateSignalOptions<T | undefined> | undefined): [WritableApiSignal<T>, WritableSignal<Error | undefined>] {
  const valueSignal: WritableApiSignal<T> = signal(undefined, options) as WritableApiSignal<T>;
  const errorSignal: WritableSignal<Error | undefined> = signal(undefined);
  valueSignal[apiErrorSymbol] = errorSignal;

  return [valueSignal, errorSignal];
}

async function fetchObservableForSignal<T>(
  source: Observable<T>,
  valueSignal: WritableApiSignal<T>,
  errorSignal: WritableSignal<Error | undefined>
) {
  const value: T | undefined = await firstValueErrorHandled(source, errorSignal);
  valueSignal.set(value);
  if (value) {
    errorSignal.set(undefined);
  }
}

function firstValueErrorHandled<T>(source: Observable<T>, errorSignal: WritableSignal<Error | undefined>): Promise<T | undefined> {
  return firstValueFrom(
    source.pipe(
      catchError(error => {
        LOG.error(error);
        errorSignal.set(error);
        return of(undefined);
      })
    )
  );
}
